import { apiGet, apiPatch, apiPost } from '../../../shared/api-client/index.js';
import {
  entityDetailKey,
  entityListKey,
  type EntityListKey,
} from '../../../shared/api/entityQueryKeys.js';

export interface AutomationItem {
  id: string;
  name: string;
  description: string | null;
  triggerType: 'manual' | 'event' | 'scheduled';
  isActive: boolean;
  /** Server-owned discriminator for a catalog projection without a persisted Scenario row. */
  isProjected: boolean;
  /** Stable semantic key equal to Scenario.config.id; null only when no usable id. Unknown keys allowed. */
  presetKey: string | null;
}

export interface ScenarioDetail extends AutomationItem {
  config: unknown;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export interface PatchScenarioPayload {
  name?: string;
  description?: string | null;
  triggerType?: 'manual' | 'event' | 'scheduled';
  config?: unknown;
  isActive?: boolean;
  expectedVersion?: number;
}

export interface CreateWorkflowGraphScenarioPayload {
  kind: 'workflow_graph';
  name: string;
  description?: string | null;
  config: unknown;
}

export function getAutomationsQueryKey(workspaceId: string): EntityListKey {
  return entityListKey('automations', workspaceId, {});
}

export function getScenarioQueryKey(workspaceId: string, scenarioId: string): readonly string[] {
  return entityDetailKey('scenario', workspaceId, scenarioId);
}

export async function listAutomations(): Promise<AutomationItem[]> {
  return apiGet<AutomationItem[]>('/scenarios');
}

export async function getScenario(scenarioId: string): Promise<ScenarioDetail> {
  return apiGet<ScenarioDetail>(`/scenarios/${scenarioId}`);
}

export async function setAutomationActive(
  scenarioId: string,
  isActive: boolean,
): Promise<AutomationItem> {
  return apiPatch<AutomationItem, { isActive: boolean }>(`/scenarios/${scenarioId}`, {
    isActive,
  });
}

export async function patchScenario(
  scenarioId: string,
  payload: PatchScenarioPayload,
): Promise<ScenarioDetail> {
  return apiPatch<ScenarioDetail, PatchScenarioPayload>(`/scenarios/${scenarioId}`, payload);
}

export async function createScenarioFromPreset(
  presetKey: string,
  name?: string,
): Promise<ScenarioDetail> {
  return apiPost<ScenarioDetail, { presetKey: string; name?: string }>('/scenarios', {
    presetKey,
    ...(name === undefined ? {} : { name }),
  });
}

export async function createWorkflowGraphScenario(
  payload: CreateWorkflowGraphScenarioPayload,
): Promise<ScenarioDetail> {
  return apiPost<ScenarioDetail, CreateWorkflowGraphScenarioPayload>('/scenarios', payload);
}

export async function duplicateScenario(scenarioId: string): Promise<ScenarioDetail> {
  return apiPost<ScenarioDetail, Record<string, never>>(`/scenarios/${scenarioId}/duplicate`, {});
}
